import {
  activateSubscriptionWeek,
  optInToCampaign,
  optOutOfCampaign,
} from '@/clients/pops/client'
import { $loggedInSettings } from '@/stores/logged-in-settings/stores'
import { loadSubscriptionWeek } from '@/stores/meal-program-subscription/operations'
import {
  $campaignWeeks,
  $subscription,
  $subscriptionWeeks,
} from '@/stores/meal-program-subscription/stores'
import type { Campaign as PopsCampaign } from '@/types/pops'
import type { CMSSectionCampaignProgram } from '@/types/sanity'
import type { Dayjs } from '@/utilities/dayjs-config'
import dayjs from '@/utilities/dayjs-config'
import { getMealsArrayFromSku, getWeeksArray } from '@/utilities/sakara'

import type { CampaignWeek } from './campaign-week'
import { DeliveryZoneException, type DeliveryZone } from './delivery-zone'
import type { MealProgramSubscription } from './meal-program-subscription'
import { openDialog } from '@/stores/dialog/actions'

/* eslint-disable no-unused-vars */

export const getCampaignConfig = (
  campaign: PopsCampaign,
): CMSSectionCampaignProgram | undefined => {
  const { id, identifier } = campaign
  const campaignConfigs = $loggedInSettings.get()?.programs.campaigns
  return campaignConfigs?.find((campaign) =>
    campaign.campaignIdentifier
      ? campaign.campaignIdentifier === identifier
      : campaign.campaignId === id,
  )
}

export const getCleanseConfig = (): CMSSectionCampaignProgram | undefined => {
  const campaignConfigs = $loggedInSettings.get()?.programs.campaigns
  return campaignConfigs?.find((campaign) => campaign.campaignId === null)
}

export class Campaign {
  id: number
  name: string
  skuSuffix: string
  programLength: number
  startWeeks: Dayjs[]
  optInWeeks: Dayjs[]
  minimumDays: number
  minimumMeals: number
  detoxAvailableForSubs: boolean
  detoxPlusWeeks: Dayjs[]
  soldOutStartWeeks: Dayjs[]
  soldOutDetoxPlusWeeks: Dayjs[]
  optInStatus?: { campaignStartWeek: Dayjs }
  cleanseCampaign: boolean
  config?: CMSSectionCampaignProgram
  allowOptOut: boolean

  constructor(rawCampaign: PopsCampaign) {
    this.id = rawCampaign.id
    this.name = rawCampaign.name
    this.skuSuffix = rawCampaign.sku_suffix
    this.programLength = rawCampaign.program_length
    this.startWeeks = rawCampaign.start_weeks
      .map((week) => dayjs(week))
      .sort((weekA, weekB) => weekA.unix() - weekB.unix())
    this.optInWeeks = this.startWeeks
    this.minimumDays = rawCampaign.minimum_days
    this.minimumMeals = rawCampaign.minimum_meals
    this.detoxAvailableForSubs = rawCampaign.detox_available_for_subs
    this.detoxPlusWeeks = rawCampaign.detox_plus_weeks.map((week) =>
      dayjs(week),
    )
    this.soldOutStartWeeks = rawCampaign.sold_out_start_weeks.map(
      (weekString) => dayjs(weekString),
    )
    this.soldOutDetoxPlusWeeks = rawCampaign.sold_out_detox_plus_weeks.map(
      (weekString) => dayjs(weekString),
    )
    this.allowOptOut = rawCampaign.allow_opt_out

    if (rawCampaign.opt_in_status) {
      this.optInStatus = {
        campaignStartWeek: dayjs(rawCampaign.opt_in_status.campaign_start_week),
      }
    }

    this.cleanseCampaign = rawCampaign.cleanse_campaign
    this.config = this.cleanseCampaign
      ? getCleanseConfig()
      : getCampaignConfig(rawCampaign)
  }

  get isCleanse() {
    return this.cleanseCampaign
  }

  get isFixed() {
    return this.config?.isFixed || false
  }

  get isSkipVisible() {
    return this.config?.weekFlyout?.isSkipVisible || false
  }

  get hiddenCampaignWeeksOnFlyout(): Dayjs[] {
    return this.config?.weekFlyout?.hiddenStartWeeks?.map((w) => dayjs(w)) || []
  }

  // TODO: revise this logic
  // get hiddenCampaignPlusWeeks(): Dayjs[] {
  //   if (this.#hiddenCampaignPlusWeeks) return this.#hiddenCampaignPlusWeeks
  //   if (!this.config) return []

  //   this.#hiddenCampaignPlusWeeks = this.config.hidden_campaign_plus_weeks
  //     .split('\n')
  //     .filter((w) => w)
  //     .map((w) => dayjs(w))

  //   return this.#hiddenCampaignPlusWeeks
  // }

  get mealProgramSubscriptionConfigs() {
    return null
  }

  isCampaignWeekHiddenOnFlyout(week: Dayjs) {
    return (
      this.hiddenCampaignWeeksOnFlyout.some((startWeek) =>
        startWeek.isSame(week, 'week'),
      ) || this.isWeekHiddenByDeliveryZone(week)
    )
  }

  // TODO: revise this logic
  // isCampaignPlusWeekHidden(week: Dayjs) {
  //   return (
  //     this.hiddenCampaignPlusWeeks.some((startWeek) =>
  //       startWeek.isSame(week, 'week'),
  //     ) || this.isWeekHiddenByDeliveryZone(week)
  //   )
  // }

  isWeekSoldOut(week: Dayjs) {
    return this.soldOutStartWeeks.some((startWeek) =>
      startWeek.isSame(week, 'week'),
    )
  }

  isCleanseWeekSoldOut(week: Dayjs) {
    return this.soldOutDetoxPlusWeeks.some((startWeek) =>
      startWeek.isSame(week, 'week'),
    )
  }

  isWeekHiddenByDeliveryZone(week: Dayjs) {
    const deliveryZone =
      $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]?.deliveryZone
    if (!deliveryZone) return false

    return this.getHiddenDatesForDeliveryZone(deliveryZone).some((startWeek) =>
      week.isSame(startWeek, 'week'),
    )
  }

  isCampaignWeekHiddenBecauseOfDeliveryException(week: Dayjs) {
    const overlappedWeeks = this.getOverlappedWeeks(week)
    const isOverlapWithException = overlappedWeeks.some((w) => {
      const subscriptionWeek = $subscriptionWeeks.get()[w.format('YYYY-MM-DD')]
      return subscriptionWeek?.deliveryZone instanceof DeliveryZoneException
    })
    if (!isOverlapWithException) return false

    return !this.config?.isHolidayOptInAllowed
  }

  getOverlappedWeeks(startWeek: Dayjs) {
    return getWeeksArray(startWeek, this.programLength)
  }

  static optOutOfCampaignsForWeeks(weeks: Dayjs[]) {
    const subscriptionId = $subscription.get()?.id as number

    return Promise.all(
      weeks.map((week) => {
        const campaignWeek = $campaignWeeks.get()[week.format('YYYY-MM-DD')]

        if (!campaignWeek) return Promise.resolve()

        return optOutOfCampaign(subscriptionId, campaignWeek.campaignId)
      }),
    )
  }

  getCleanseWeekOptions(_startWeek: Dayjs) {
    return []
  }

  getHiddenDatesForDeliveryZone(_deliveryZone: DeliveryZone) {
    return []
  }

  // Remove all campaigns and systems that this campaign overlaps
  async optOutOfOverlappedPrograms({ startWeek }: { startWeek: Dayjs }) {
    const affectedWeeks = this.getOverlappedWeeks(startWeek)
    await Campaign.optOutOfCampaignsForWeeks(affectedWeeks)
  }

  async optOut({ campaignWeek }: { campaignWeek: CampaignWeek }) {
    const { campaignStartWeek, campaignId } = campaignWeek
    const subscription = $subscription.get() as MealProgramSubscription
    const { id: subscriptionId } = subscription

    await loadSubscriptionWeek({
      week: campaignStartWeek,
    })

    const subscriptionWeek =
      $subscriptionWeeks.get()[campaignStartWeek.format('YYYY-MM-DD')]

    if (!subscriptionWeek.isModifiable) return Promise.resolve()

    return optOutOfCampaign(subscriptionId, campaignId)
  }

  async optIn({
    startWeek,
    isDualOptIn: _ = false,
  }: {
    startWeek: Dayjs
    isDualOptIn: boolean
  }) {
    const affectedWeeks = this.getOverlappedWeeks(startWeek)
    const subscriptionId = $subscription.get()!.id
    const subscriptionWeek =
      $subscriptionWeeks.get()[startWeek.format('YYYY-MM-DD')]
    const { daysOfFood, meals } = subscriptionWeek

    await this.optOutOfOverlappedPrograms({ startWeek })

    // await setWeekSku(subscriptionId, startWeek, '5BLD');

    if (!this.isCleanse) {
      if (meals.length < this.minimumMeals && daysOfFood < this.minimumDays) {
        openDialog('campaign-upgraded-meals-and-days', {
          dialogContent:
            this.config?.secondaryPopups?.upgradedMealsAndDaysDialog,
        })
      } else if (meals.length < this.minimumMeals) {
        openDialog('campaign-upgraded-meals', {
          dialogContent: this.config?.secondaryPopups?.upgradedMealsDialog,
        })
      } else if (daysOfFood < this.minimumDays) {
        openDialog('campaign-upgraded-days', {
          dialogContent: this.config?.secondaryPopups?.upgradedDaysDialog,
        })
      }

      await Promise.all(
        affectedWeeks.map(async (affectedWeek) => {
          await loadSubscriptionWeek({
            week: affectedWeek,
          })
          await activateSubscriptionWeek(
            subscriptionId,
            affectedWeek.format('YYYY-MM-DD'),
          )
        }),
      )
    }

    return optInToCampaign(
      subscriptionId,
      this.id,
      startWeek.format('YYYY-MM-DD'),
    )
  }

  async onWeekSkip({ campaignWeek: _ }: { campaignWeek: CampaignWeek }) {
    openDialog('campaign-skip-notice', {
      dialogContent: this.config?.secondaryPopups?.skipNoticeDialog,
    })
    return Promise.reject()
  }

  async onWeekSkuChange({
    campaignWeek,
    sku,
  }: {
    campaignWeek: CampaignWeek
    sku: string
  }) {
    const meals = getMealsArrayFromSku(sku)

    await loadSubscriptionWeek({ week: campaignWeek.campaignStartWeek })
    const subscriptionWeek =
      $subscriptionWeeks.get()[
        campaignWeek.campaignStartWeek.format('YYYY-MM-DD')
      ]
    const { isModifiable } = subscriptionWeek

    if (meals.length < this.minimumMeals) {
      if (isModifiable) {
        openDialog('campaign-downgrade', {
          dialogContent: this.config?.secondaryPopups?.downgradeDialog,
        })
      } else {
        openDialog('campaign-downgrade-after-start', {
          dialogContent:
            this.config?.secondaryPopups?.downgradeAfterStartDialog,
        })
      }

      return Promise.reject()
    }

    return Promise.resolve()
  }
}
/* eslint-enable no-unused-vars */
